import React, { useEffect, useMemo, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import './contact-us.css';
import Footer from '../../layout/footer/footer';
import { getContactConfigs } from '../../services/configService';
import { sendContactMessage } from '../../services/messageService';

const unwrapPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  let current = payload;
  const visited = new Set();

  while (
    current &&
    typeof current === 'object' &&
    'data' in current &&
    current.data !== current &&
    !visited.has(current)
  ) {
    visited.add(current);
    current = current.data;
  }

  return current;
};

const extractValue = (source, keys) => {
  if (!source) {
    return '';
  }

  const normalizedKeys = keys.map((key) => key.toLowerCase());

  const resolveEntryValue = (entry) => {
    if (!entry || typeof entry !== 'object') {
      return '';
    }

    const candidates = [
      entry.value,
      entry.configValue,
      entry.val,
      entry.valueFa,
      entry.description,
      entry.content,
    ];

    return candidates.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '') ?? '';
  };

  if (Array.isArray(source)) {
    const match = source.find((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }

      const keyCandidate =
        item.key ?? item.configKey ?? item.name ?? item.fieldName ?? item.title;

      return (
        typeof keyCandidate === 'string' &&
        normalizedKeys.includes(keyCandidate.toLowerCase())
      );
    });

    return resolveEntryValue(match);
  }

  const entries = Object.entries(source);
  const directMatch = entries.find(([key]) =>
    normalizedKeys.includes(String(key).toLowerCase()),
  );

  if (directMatch) {
    return directMatch[1];
  }

  for (const [, value] of entries) {
    if (!value || typeof value !== 'object') {
      continue;
    }

    const nestedValue = extractValue(value, keys);
    if (nestedValue) {
      return nestedValue;
    }
  }

  return '';
};

const buildSocialLink = (value, type) => {
  if (!value) {
    return '';
  }

  const trimmedValue = String(value).trim();

  if (type === 'email') {
    return trimmedValue.startsWith('mailto:')
      ? trimmedValue
      : `mailto:${trimmedValue}`;
  }

  if (trimmedValue.startsWith('http://') || trimmedValue.startsWith('https://')) {
    return trimmedValue;
  }

  if (type === 'telegram') {
    const username = trimmedValue.replace(/^@/, '');
    return `https://t.me/${username}`;
  }

  if (type === 'whatsapp') {
    const digits = trimmedValue.replace(/[^0-9+]/g, '');
    const withoutPlus = digits.startsWith('+') ? digits.slice(1) : digits;
    return `https://wa.me/${withoutPlus}`;
  }

  if (type === 'instagram') {
    const handle = trimmedValue.replace(/^@/, '');
    return `https://instagram.com/${handle}`;
  }

  return trimmedValue;
};

const ContactUs = () => {
  const [contactInfo, setContactInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });
  const [formValues, setFormValues] = useState({
    title: '',
    fullName: '',
    email: '',
    mobile: '',
    note: '',
  });

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        setLoading(true);
        const payload = await getContactConfigs();
        const configs = unwrapPayload(payload);
        console.log('=======================================')
        console.log(payload)
        console.log(configs)
        const primaryConfig = Array.isArray(configs?.items) && configs.items.length > 0
          ? configs.items[0]
          : configs;

        const info = {
          address:  primaryConfig["address"], //extractValue(primaryConfig, ['address', 'آدرس']),
          phone: primaryConfig["phone"], //extractValue(primaryConfig, ['phone', 'tel', 'تلفن']),
          mobile:  primaryConfig["mobile"], //extractValue(primaryConfig, ['mobile', 'موبایل']),
          telegram: extractValue(primaryConfig, ['telegram', 'تلگرام']),
          whatsapp: extractValue(primaryConfig, ['whatsapp', 'واتساپ', 'واتس اپ', 'واتس‌اپ']),
          instagram: extractValue(primaryConfig, ['instagram', 'اینستاگرام']),
          eitaa:  primaryConfig["eitaa"], //extractValue(primaryConfig, ['eitaa', 'ایتا']),
          email:  primaryConfig["email"], //extractValue(primaryConfig, ['email', 'ایمیل']),
        };

        setContactInfo(info);
        console.log(info)
        setError(false);
      } catch (err) {
        console.error('Failed to load contact configs', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, []);

  const mapQuery = useMemo(() => {
    const fallback = 'Tehran, Iran';
    return encodeURIComponent(contactInfo.address || fallback);
  }, [contactInfo.address]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    resetStatus();
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetStatus = () => setSubmitStatus({ type: null, message: '' });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    try {
      resetStatus();
      setSubmitting(true);
      await sendContactMessage({
        title: formValues.title.trim(),
        fullName: formValues.fullName.trim(),
        email: formValues.email.trim(),
        mobile: formValues.mobile.trim(),
        note: formValues.note.trim(),
      });

      setSubmitStatus({
        type: 'success',
        message: 'پیام شما با موفقیت ارسال شد.',
      });
      setFormValues({
        title: '',
        fullName: '',
        email: '',
        mobile: '',
        note: '',
      });
    } catch (err) {
      console.error('Failed to send contact message', err);
      setSubmitStatus({
        type: 'error',
        message: 'ارسال پیام با خطا مواجه شد. لطفاً مجدداً تلاش کنید.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const socials = [
    { key: 'telegram', icon: 'fab fa-telegram-plane', type: 'telegram', label: 'تلگرام' },
    { key: 'whatsapp', icon: 'fab fa-whatsapp', type: 'whatsapp', label: 'واتساپ' },
    { key: 'instagram', icon: 'fab fa-instagram', type: 'instagram', label: 'اینستاگرام' },
    // { key: 'email', icon: 'fas fa-envelope', type: 'email', label: 'ایمیل' },
    // { key: 'eitaa', icon: 'fa-eitaa-green', type: 'eitaa', label: 'ایتا' },
  ];

  return (
    <>
      <div className="contact-page">
        <div className="container" style={{marginTop:'35px'}}>
          <h1 className="contact-title">تماس با ما</h1>
        </div>

        <div className="contact-map-wrapper">
          <div className="container">
            <div className="contact-map">
              <iframe
                title="محل شرکت"
                src={`https://www.google.com/maps?q=35.70504164075272,51.41472601489383&output=embed`}
                loading="lazy"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        <div className="container">
          <div className="contact-sections">
            <div className="contact-form-card">
              <h2 className="section-title">تماس با ما</h2>
              <p className="section-subtitle">
                فرم زیر را تکمیل کنید تا در کوتاه‌ترین زمان با شما تماس بگیریم.
              </p>
              <Form className="contact-form" onSubmit={handleSubmit}>
                <Form.Group controlId="contactTitle">
                  <Form.Label>عنوان</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="موضوع پیام"
                    value={formValues.title}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </Form.Group>
                <Form.Group controlId="contactName">
                  <Form.Label>نام</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    placeholder="نام و نام خانوادگی"
                    value={formValues.fullName}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </Form.Group>
                <Form.Group controlId="contactEmail">
                  <Form.Label>ایمیل</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="example@mail.com"
                    value={formValues.email}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </Form.Group>
                <Form.Group controlId="mobile">
                  <Form.Label>تلفن</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    placeholder="09123456789"
                    value={formValues.mobile}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </Form.Group>
                <Form.Group controlId="note">
                  <Form.Label>متن پیام</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="note"
                    placeholder="پیام خود را بنویسید"
                    value={formValues.note}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </Form.Group>
                <button type="submit" className="contact-submit" disabled={submitting}>
                  {submitting ? 'در حال ارسال...' : 'ارسال پیام'}
                </button>
                {submitStatus.message && (
                  <p
                    className={`contact-submit-status ${
                      submitStatus.type === 'success' ? 'status-success' : 'status-error'
                    }`}
                  >
                    {submitStatus.message}
                  </p>
                )}
              </Form>
            </div>

            <div className="contact-info-card">
              <h2 className="section-title">اطلاعات تماس</h2>
              {error ? (
                <p className="contact-error">متأسفانه در دریافت اطلاعات تماس خطایی رخ داد.</p>
              ) : (
                <>
                  <ul className="contact-info-list">
                    {contactInfo.address && (
                      <li>
                        <span className="info-icon">
                          <i className="fas fa-location-dot" aria-hidden="true" />
                        </span>
                        <div>
                          <span className="info-label"><span className="info-value mx-2">{contactInfo.address}</span> </span>
                          
                        </div>
                      </li>
                    )}
                    {contactInfo.phone && (
                      <li>
                        <span className="info-icon">
                          <i className="fas fa-phone" aria-hidden="true" />
                        </span>
                        <div>
                          <span className="info-label">
                            <a href={`tel:${contactInfo.phone}`} className="info-value mx-2">
                            {contactInfo.phone}
                          </a>
                          </span>
                          
                        </div>
                      </li>
                    )}
                    {contactInfo.mobile && (
                      <li>
                        <span className="info-icon">
                          <i className="fas fa-mobile-screen" aria-hidden="true" />
                        </span>
                        <div>
                          <span className="info-label"><a href={`tel:${contactInfo.mobile}`} className="info-value">
                            {contactInfo.mobile}
                          </a> </span>
                          
                        </div>
                      </li>
                    )}
                    {contactInfo.eitaa && (
                      <li>
                        <span className="info-icon">
                         <span className='fa-eitaa-green  w-30px h-30px' />
                        </span>
                        <div>
                          <span className="info-label"><a href={`${contactInfo.eitaa}`} className="info-value">
                            {contactInfo.eitaa}
                          </a> </span>
                          
                        </div>
                      </li>
                    )}
                    {contactInfo.email && (
                      <li>
                        <span className="info-icon">
                          <i className="fas fa-envelope" aria-hidden="true" />
                        </span>
                        <div>
                          <span className="info-label"><a href={`mailto::${contactInfo.eitaa}`} className="info-value">
                            {contactInfo.email}
                          </a> </span>
                          
                        </div>
                      </li>
                    )}
                  </ul>

                  <div className="contact-socials" aria-label="شبکه‌های اجتماعی">
                    {socials.map((social) => {
                      const value = contactInfo[social.key];
                      if (!value) {
                        return null;
                      }

                      const href = buildSocialLink(value, social.type);
                      if (!href) {
                        return null;
                      }

                      return (
                        <a
                          key={social.key}
                          href={href}
                          className="contact-social-link"
                          target={social.type === 'email' ? undefined : '_blank'}
                          rel={social.type === 'email' ? undefined : 'noopener noreferrer'}
                          title={social.label}
                          aria-label={social.label}
                        >
                         {social.key == '???'? <span className='fa-eitaa  w-30px h-30px' /> : <i className={social.icon} aria-hidden="true" />} 
                        </a>
                      );
                    })}
                  </div>

                  {loading && (
                    <p className="contact-loading">در حال بارگذاری اطلاعات تماس...</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="home-footer mt-40">
        <Footer />
      </div>
    </>
  );
};

export default ContactUs;
